import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private refreshAfter401Promise: Promise<string | null> | null = null;

  constructor(private readonly authService: AuthService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.shouldManageToken(req)) {
      return next.handle(req);
    }

    return from(this.authService.ensureValidAccessToken()).pipe(
      switchMap((token) => next.handle(this.withBearerToken(req, token))),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || !this.shouldManageToken(req)) {
          return throwError(() => error);
        }

        return from(this.refreshAfter401()).pipe(
          switchMap((token) => {
            if (!token) {
              return throwError(() => error);
            }
            return next.handle(this.withBearerToken(req, token));
          })
        );
      })
    );
  }

  private shouldManageToken(req: HttpRequest<unknown>): boolean {
    if (!req.url.startsWith(environment.apiBaseUrl)) {
      return false;
    }
    if (/\/auth\/(?:identity\/session\/refresh|login\/entra\/refresh|refresh-token)\b/i.test(req.url)) {
      return false;
    }
    return (
      req.headers.has('Authorization') || req.headers.get('auth-req') === 'true'
    );
  }

  private withBearerToken(
    req: HttpRequest<unknown>,
    token: string | null
  ): HttpRequest<unknown> {
    if (!token) {
      return req;
    }
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    const orgHeader = this.buildOrganizationHeader(token);
    if (orgHeader) {
      Object.assign(headers, orgHeader);
    }
    return req.clone({ setHeaders: headers });
  }

  private buildOrganizationHeader(
    token: string | null
  ): Record<string, string> | null {
    if (!token || !this.authService.isIdentityAccessToken(token)) {
      return null;
    }
    const orgId = this.authService.resolveActiveOrganizationId();
    return orgId ? { 'X-Organization-Id': orgId } : null;
  }

  private refreshAfter401(): Promise<string | null> {
    if (!this.refreshAfter401Promise) {
      this.refreshAfter401Promise = this.authService
        .refreshSession()
        .finally(() => {
          this.refreshAfter401Promise = null;
        });
    }
    return this.refreshAfter401Promise;
  }
}
